import {
  DataTable,
  type TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react"
import { useOpenCandidateCv } from "../../hooks/useOpenCandidateCv"
import type {
  CandidateSummaryDto,
  CandidateApplicationSummaryDto,
} from "../../infrastructure/candidate-service"

const STAGE_BADGE_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  Applied: "secondary",
  Screened: "secondary",
  Candidate: "default",
  Hired: "default",
  Rejected: "destructive",
  Withdrawn: "outline",
}

export function StageBadge({ stage }: { stage: string }) {
  return (
    <Badge variant={STAGE_BADGE_VARIANT[stage] ?? "outline"} className="text-xs">
      {stage}
    </Badge>
  )
}

function ScoreCell({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) {
    return <span className="text-muted-foreground">—</span>
  }
  return <span>{score}</span>
}

function ApplicationsSummary({
  applications,
}: {
  applications: CandidateApplicationSummaryDto[]
}) {
  if (!applications.length) {
    return <span className="text-muted-foreground text-xs">None</span>
  }
  return (
    <div className="space-y-1">
      {applications.map((app) => (
        <div key={app.applicationId} className="flex items-center gap-1.5 text-xs">
          <span className="truncate max-w-[140px]" title={app.vacancyTitle}>
            {app.vacancyTitle}
          </span>
          {app.stage && <StageBadge stage={app.stage} />}
        </div>
      ))}
    </div>
  )
}

function SkillsCell({ skills }: { skills: string[] }) {
  if (!skills.length) {
    return <span className="text-muted-foreground text-xs">—</span>
  }
  const display = skills.slice(0, 3)
  const remaining = skills.length - display.length

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-wrap gap-1 cursor-default">
            {display.map((s) => (
              <Badge key={s} variant="outline" className="text-xs font-normal">
                {s}
              </Badge>
            ))}
            {remaining > 0 && (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                +{remaining}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs text-xs">{skills.join(", ")}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function CandidatesTableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  )
}

// Generated DTO fields are optional; DataTable requires an id
type CandidateRow = CandidateSummaryDto & { id: string }

interface Props {
  candidates: CandidateSummaryDto[]
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onSelectCandidate: (candidateId: string) => void
}

export function CandidatesTable({
  candidates,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onSelectCandidate,
}: Props) {
  const { openCv, openingCandidateId } = useOpenCandidateCv()
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const columns: TableColumn<CandidateRow>[] = [
    {
      key: "name",
      label: "Name",
      headerClassName: "px-3 sm:px-6 min-w-[180px]",
      cellClassName: "px-3 sm:px-6",
      render: (candidate) => (
        <div>
          <button
            type="button"
            className="font-medium text-blue-700 hover:text-blue-800 hover:underline text-left"
            onClick={(e) => {
              e.stopPropagation()
              onSelectCandidate(candidate.id)
            }}
          >
            {candidate.firstName} {candidate.lastName}
          </button>
          <div className="text-xs text-muted-foreground break-all">{candidate.email}</div>
          <div className="text-xs text-muted-foreground md:hidden">
            {[
              candidate.location,
              candidate.experienceYears
                ? `${candidate.experienceYears} yr${candidate.experienceYears !== 1 ? "s" : ""}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      ),
    },
    {
      key: "location",
      label: "Location",
      headerClassName: "hidden md:table-cell",
      cellClassName: "hidden md:table-cell",
      render: (candidate) =>
        candidate.location ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: "skills",
      label: "Skills",
      headerClassName: "hidden lg:table-cell",
      cellClassName: "hidden lg:table-cell",
      render: (candidate) => <SkillsCell skills={candidate.skills ?? []} />,
    },
    {
      key: "experienceYears",
      label: "Experience",
      headerClassName: "hidden sm:table-cell",
      cellClassName: "hidden sm:table-cell",
      render: (candidate) =>
        candidate.experienceYears === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          `${candidate.experienceYears} yr${candidate.experienceYears !== 1 ? "s" : ""}`
        ),
    },
    {
      key: "applications",
      label: "Vacancies Applied",
      headerClassName: "px-3 sm:px-6",
      cellClassName: "px-3 sm:px-6",
      render: (candidate) => (
        <ApplicationsSummary applications={candidate.applications ?? []} />
      ),
    },
    {
      key: "matchScore",
      label: "AI Score",
      render: (candidate) => (
        <ScoreCell score={candidate.applications?.[0]?.matchScore ?? null} />
      ),
    },
    {
      key: "cvUrl",
      label: "CV",
      headerClassName: "hidden xl:table-cell",
      cellClassName: "hidden xl:table-cell",
      render: (candidate) =>
        candidate.cvUrl ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              void openCv(candidate.id)
            }}
            disabled={openingCandidateId === candidate.id}
            title={candidate.cvFileName ?? undefined}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[200px] disabled:opacity-50"
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate">{candidate.cvFileName ?? "CV"}</span>
          </button>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-3 min-w-0">
      <DataTable
        columns={columns}
        data={candidates.filter((c): c is CandidateRow => !!c.id)}
        emptyMessage="No candidates match the active filters."
        onRowClick={(candidate) => onSelectCandidate(candidate.id)}
      />

      {candidates.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages} ({totalCount} total)
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
